import { Card, CardContent, Grid2, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { defineMessages, useIntl, type MessageDescriptor } from 'react-intl';

import Icon from '@components/Icon/Icon';
import AlertSettingDialogListener from '@components/Modal/Content/AlertSettingModalContent/FormDialog';

import { useFeatureFlags } from '@hooks/useFeatureFlags';

import type { AlertSettingModel } from 'generated/types';

import Header from './Header';
import NotificationToggle from './NotificationToggle';
import ParameterList from './Parameter';

const messages = defineMessages({
  parameterTitle: {
    id: 'alertSettingTable.parameters.title',
    defaultMessage: 'Alert Parameters',
  },
  parameterCaption: {
    id: 'alertSettingTable.parameters.caption',
    defaultMessage:
      'Alerts will be created when the patient’s measurements are within these ranges:',
  },
  recipientTitle: {
    id: 'alertSettingTable.recipients.title',
    defaultMessage: 'Recipients',
  },
  recipientCaption: {
    id: 'alertSettingTable.recipients.caption',
    defaultMessage:
      'Notifications about the alert will be sent to the following recipients:',
  },
});

interface AlertSettingItemProps {
  setting: AlertSettingModel;
}

export default function AlertSettingItem({ setting }: AlertSettingItemProps) {
  const { formatMessage } = useIntl();
  const { flags } = useFeatureFlags();
  const { palette } = useTheme();

  const {
    alert_parameters,
    alert_setting_id,
    notification_enabled,
    name: code,
    recipients,
  } = setting;

  const editIcon = flags.isAlertSettingUpdateAlertSettingEnabled ? (
    <AlertSettingDialogListener data={setting}>
      {({ handleOpen }) => (
        <Icon
          color="text.secondary"
          name="pencil"
          onClick={handleOpen}
          type="fas"
        />
      )}
    </AlertSettingDialogListener>
  ) : null;

  const displayHeader = (
    title: MessageDescriptor,
    caption: MessageDescriptor
  ) => {
    return (
      <>
        <Stack
          alignContent="center"
          direction="row"
          justifyContent="space-between"
        >
          <Typography variant="body1">{formatMessage(title)}</Typography>
          {editIcon}
        </Stack>
        <Typography variant="caption">{formatMessage(caption)}</Typography>
      </>
    );
  };

  return (
    <Card sx={{ my: 0.5 }}>
      <Header setting={setting} />
      <CardContent sx={{ mx: 1 }}>
        <Grid2 container columnSpacing={4} rowSpacing={3}>
          {/** Receive notifications toggle */}
          <Grid2 size={{ xs: 12 }}>
            <NotificationToggle
              alert_setting_id={alert_setting_id}
              enabled={notification_enabled}
              parameters={alert_parameters}
            />
          </Grid2>

          {/** Alert parameters */}
          <Grid2 size={{ xs: 12, sm: 6 }}>
            {displayHeader(messages.parameterTitle, messages.parameterCaption)}
            <ParameterList code={code} parameters={alert_parameters} />
          </Grid2>

          {/** Alert recipients */}
          <Grid2 size={{ xs: 12, sm: 6 }}>
            {displayHeader(messages.recipientTitle, messages.recipientCaption)}
            <ul>
              {recipients.map((r) => (
                <li
                  color={palette.text.secondary}
                  key={`alert-recipient-${r.account_id}-${r.user_id}`}
                >
                  <Typography color="text.secondary" variant="subtitle2">
                    {r.name}
                  </Typography>
                </li>
              ))}
            </ul>
          </Grid2>
        </Grid2>
      </CardContent>
    </Card>
  );
}
